import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Edit3, Plus, Trash2, Users, FileText, Building2, RefreshCw, Paperclip, Search, Wallet, Mail, MailPlus, Download, ArrowUpDown, AlertTriangle, XCircle, Layers, History } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { downloadFile, formatApiError } from "../lib/api";
import { money } from "../lib/format";
import { StatusPill } from "./DashboardPage";
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PasswordField } from "../components/PasswordField";
import { AttachmentsDialog, AttachmentsPanel } from "../components/InvoiceAttachments";
import ProgramFinancials from "../components/ProgramFinancials";
import BookSummary from "../components/BookSummary";
import AdminFinancials from "./AdminFinancials";
import { ActionIcon } from "../components/ActionIcon";
import RatesManager from "../components/RatesManager";
import AdminAch from "../components/AdminAch";
import { PaymentGroups } from "../components/PaymentGroups";
import { useRelease } from "../lib/release";
import { useAuth } from "../lib/auth";
import { MicrosoftLinkCard } from "../components/sso/MicrosoftLinkCard";
import { SigninPanelCard } from "../components/SigninPanelCard";
import { BookVisibilityCard } from "../components/BookVisibilityCard";
import { GoLiveCard } from "../components/launch/GoLiveCard";
import { LinkedSiteCard } from "../components/launch/LinkedSiteCard";
import { Tooltip as UiTooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";

const INVOICE_PAGE_SIZE = 25;
const INVOICE_STATUSES = ["pending", "approved", "paid", "rejected"];
const ADMIN_SETTABLE = ["pending", "paid"];

/* --- Admin overview --- */
const ATTENTION_DEFAULTS = { stale_days: 7, utilization_pct: 85, available_pct: 10 };

function AttentionStrip() {
  const [thresholds, setThresholds] = useState(ATTENTION_DEFAULTS);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let active = true;
    api.get("/admin/portfolio/attention", { params: thresholds })
      .then(({ data: payload }) => { if (active) setData(payload); })
      .catch(() => { if (active) setData(null); });
    return () => { active = false; };
  }, [thresholds]);

  const flagged = data?.flagged || [];

  return (
    <div className="border mb-8" style={{ borderColor: "var(--border)", background: flagged.length ? "#FBF7F2" : "var(--surface)" }} data-testid="attention-strip">
      <div className="px-6 py-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <AlertTriangle size={18} style={{ color: flagged.length ? "#E8871E" : "var(--muted)" }} className="mt-0.5"/>
          <div>
            <div className="eyebrow">Needs attention</div>
            <div className="font-heading font-bold text-lg mt-0.5" style={{ color: "var(--ink)" }} data-testid="attention-headline">
              {flagged.length
                ? `${flagged.length} employer${flagged.length === 1 ? "" : "s"} to look at`
                : "Nothing needs attention right now"}
            </div>
            <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>
              Invoices in review over {thresholds.stale_days} days · utilization at or above {thresholds.utilization_pct}% ·
              available refund at or below {thresholds.available_pct}% of the balance
            </p>
          </div>
        </div>
        <button onClick={() => setOpen((v) => !v)} className="btn-ghost text-xs self-start" data-testid="attention-thresholds-toggle">
          {open ? "Hide thresholds" : "Adjust Thresholds"}
        </button>
      </div>

      {open && (
        <div className="px-6 pb-6 grid grid-cols-1 sm:grid-cols-3 gap-5" data-testid="attention-thresholds">
          <div>
            <label className="field-label">Days in review</label>
            <input type="number" min={1} max={120} className="input-flat" value={thresholds.stale_days}
                   onChange={(e) => setThresholds((t) => ({ ...t, stale_days: Number(e.target.value) || 1 }))}
                   data-testid="threshold-stale-days"/>
          </div>
          <div>
            <label className="field-label">Utilization at or above (%)</label>
            <input type="number" min={0} max={100} className="input-flat" value={thresholds.utilization_pct}
                   onChange={(e) => setThresholds((t) => ({ ...t, utilization_pct: Number(e.target.value) || 0 }))}
                   data-testid="threshold-utilization"/>
          </div>
          <div>
            <label className="field-label">Available at or below (%)</label>
            <input type="number" min={0} max={100} className="input-flat" value={thresholds.available_pct}
                   onChange={(e) => setThresholds((t) => ({ ...t, available_pct: Number(e.target.value) || 0 }))}
                   data-testid="threshold-available"/>
          </div>
          <button className="btn-ghost text-xs justify-self-start" onClick={() => setThresholds(ATTENTION_DEFAULTS)} data-testid="threshold-reset">
            Reset To Defaults
          </button>
        </div>
      )}

      {Boolean(flagged.length) && (
        <div className="border-t grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3" style={{ borderColor: "var(--border)" }}>
          {flagged.map((client) => (
            <Link
              key={client.id}
              to={`/admin/invoices?company=${encodeURIComponent(client.company)}`}
              className="p-5 border-b md:border-r hover:bg-white transition-colors"
              style={{ borderColor: "var(--border)" }}
              data-testid={`attention-client-${client.id}`}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="font-medium" style={{ color: "var(--ink)" }}>{client.company || client.name}</span>
                {client.invoice_service === false && (
                  <span className="pill text-[9px]" style={{ borderColor: "var(--border-strong)", color: "var(--muted)" }} data-testid={`no-invoice-service-${client.id}`}>
                    No Invoice Payments
                  </span>
                )}
                <span className="font-heading font-bold text-sm" style={{ color: "var(--ink)" }}>{client.utilization_pct}%</span>
              </div>
              <ul className="mt-2 space-y-1">
                {client.reasons.map((reason) => (
                  <li key={reason.kind} className="text-sm flex items-center gap-2" style={{ color: "var(--muted)" }}>
                    <span className="w-1.5 h-1.5" style={{ background: "#E8871E" }}/> {reason.label}
                  </li>
                ))}
              </ul>
              <div className="mt-3 text-xs eyebrow" style={{ color: "var(--accent)" }}>Review invoices →</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminOverview() {
  const [portfolio, setPortfolio] = useState(null);
  const [pending, setPending] = useState([]);
  const [sortKey, setSortKey] = useState("err_balance");
  const [sortAsc, setSortAsc] = useState(false);

  const load = useCallback(async () => {
    const [p, i] = await Promise.all([
      api.get("/admin/portfolio"),
      api.get("/admin/invoices", { params: { status: "pending", limit: 6 } }),
    ]);
    setPortfolio(p.data);
    setPending(i.data.invoices);
  }, []);

  useEffect(() => { load().catch(e => toast.error(formatApiError(e.response?.data?.detail))); }, [load]);

  const exportPortfolio = async () => {
    try {
      await downloadFile("/admin/portfolio/export", "err-portfolio.csv");
      toast.success("Portfolio CSV downloaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const totals = portfolio?.totals || {};
  const clients = [...(portfolio?.clients || [])].sort((a, b) => {
    const left = a[sortKey], right = b[sortKey];
    const compare = typeof left === "string" ? left.localeCompare(right) : (left || 0) - (right || 0);
    return sortAsc ? compare : -compare;
  });
  const employerChartClients = [...(portfolio?.clients || [])].sort((a, b) =>
    (a.company || "").localeCompare(b.company || "", undefined, { sensitivity: "base" }));

  const sortBy = (key) => {
    if (key === sortKey) return setSortAsc((v) => !v);
    setSortKey(key);
    setSortAsc(key === "company" || key === "name");
  };

  const SortHeader = ({ label, field, align = "left" }) => (
    <th className={`px-4 py-3 ${align === "right" ? "text-right" : "text-left"}`}>
      <button onClick={() => sortBy(field)} className="eyebrow inline-flex items-center gap-1" data-testid={`sort-${field}`}>
        {label}{sortKey === field && <ArrowUpDown size={10}/>}
      </button>
    </th>
  );

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10" data-testid="admin-overview">
      <div className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="eyebrow mb-2">Administration · Portfolio</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl md:text-6xl leading-none tracking-tight" style={{ color: "var(--ink)" }}>Program Control</h1>
          <p className="mt-3 text-base max-w-xl" style={{ color: "var(--muted)" }}>
            Premium and earned refunds under management, loss ratio and invoice activity across every employer.
          </p>
        </div>
        <button onClick={exportPortfolio} className="btn-primary flex items-center gap-2 self-start" data-testid="export-portfolio">
          <Download size={14}/> Export Portfolio
        </button>
      </div>

      <AttentionStrip />

      <div className="border grid-borders grid grid-cols-2 md:grid-cols-4" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="portfolio-kpis">
        <Kpi icon={Users} label="Employers" value={totals.clients ?? 0} sub={`${totals.invoices ?? 0} invoices`}/>
        <Kpi icon={FileText} label="Premium under management" value={money(totals.premium_under_management || 0)} sub={`${totals.loss_ratio_year ?? ""} actuals`}/>
        <Kpi icon={Wallet} label="ERRs under Management" value={money(totals.err_under_management || 0)} sub={`${money(totals.available_after_disbursements || 0)} remaining after vendor disbursements`}/>
        <Kpi label="Loss Ratio" value={`${totals.loss_ratio_pct ?? 0}%`} sub="Claims + Reserves / Premiums"/>
      </div>

      {Boolean(clients.length) && (
        <div className="mt-8 border p-8" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="portfolio-utilization-chart">
          <div className="eyebrow">Loss ratio by employer</div>
          <div className="h-56 w-full mt-6">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={employerChartClients.slice(0, 12)} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                <CartesianGrid stroke="#EFEEE9" vertical={false}/>
                <XAxis dataKey="company" tick={{ fontSize: 11, fill: "#6B7A82" }} axisLine={false} tickLine={false} interval={0}/>
                <YAxis unit="%" tick={{ fontSize: 11, fill: "#6B7A82" }} axisLine={false} tickLine={false} width={44}/>
                <Tooltip formatter={(v) => [`${v}%`, "Loss ratio"]} contentStyle={{ borderRadius: 0, border: "1px solid #D8D6D1", fontSize: 12 }}/>
                <Bar dataKey="loss_ratio_pct" fill="#0E3A5C"/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="mt-8 border overflow-x-auto" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
        <table className="w-full text-sm min-w-[900px]" data-testid="portfolio-table">
          <thead>
            <tr className="border-b" style={{ borderColor: "var(--border)" }}>
              <SortHeader label="Employer" field="company"/>
              <SortHeader label="Contact" field="contact"/>
              <SortHeader label="Premium" field="premium" align="right"/>
              <SortHeader label="Loss Ratio" field="loss_ratio_pct" align="right"/>
              <SortHeader label="ERR Balance" field="err_balance" align="right"/>
              <SortHeader label="Paid" field="paid" align="right"/>
              <SortHeader label="Pending" field="pending" align="right"/>
              <SortHeader label="Available" field="available" align="right"/>
              <SortHeader label="Utilization" field="utilization_pct" align="right"/>
              <SortHeader label="Last Activity" field="last_activity" align="right"/>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr key={c.id} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`portfolio-row-${c.id}`}>
                <td className="px-4 py-3 font-medium" style={{ color: "var(--ink)" }}>{c.company || "—"}</td>
                <td className="px-4 py-3" style={{ color: "var(--muted)" }}>{c.contact || "—"}</td>
                <td className="px-4 py-3 text-right font-heading font-bold" style={{ color: "var(--ink)" }}>{money(c.premium)}</td>
                <td className="px-4 py-3 text-right font-medium" style={{ color: "var(--ink)" }}>{c.loss_ratio_pct}%</td>
                <td className="px-4 py-3 text-right" style={{ color: "var(--muted)" }}>{money(c.err_balance)}</td>
                <td className="px-4 py-3 text-right" style={{ color: "var(--muted)" }}>{money(c.paid)}</td>
                <td className="px-4 py-3 text-right" style={{ color: "var(--muted)" }}>{money(c.pending)}</td>
                <td className="px-4 py-3 text-right" style={{ color: "var(--muted)" }}>{money(c.available)}</td>
                <td className="px-4 py-3 text-right font-medium" style={{ color: "var(--ink)" }}>{c.utilization_pct}%</td>
                <td className="px-4 py-3 text-right" style={{ color: "var(--muted)" }}>{c.last_activity || "—"}</td>
              </tr>
            ))}
            {!clients.length && <tr><td colSpan={10} className="p-12 text-center text-sm" style={{ color: "var(--muted)" }} data-testid="portfolio-empty">No employer accounts yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 border" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
          <div className="p-6 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
            <div>
              <div className="eyebrow">Queue</div>
              <div className="font-heading font-bold text-xl mt-1" style={{ color: "var(--ink)" }}>Pending invoices</div>
            </div>
            <Link to="/admin/invoices" className="btn-ghost text-xs">Manage all</Link>
          </div>
          <div className="divide-y" style={{ borderColor: "var(--border)" }}>
            {pending.map(inv => (
              <div key={inv.id} className="grid grid-cols-12 items-center px-6 py-4 text-sm gap-3">
                <div className="col-span-4 font-medium" style={{ color: "var(--ink)" }}>{inv.vendor_name}</div>
                <div className="col-span-3" style={{ color: "var(--muted)" }}>{inv.user_name}</div>
                <div className="col-span-2 font-mono text-xs" style={{ color: "var(--muted)" }}>{inv.invoice_number}</div>
                <div className="col-span-3 text-right font-heading font-bold" style={{ color: "var(--ink)" }}>{money(inv.amount)}</div>
              </div>
            ))}
            {!pending.length && <div className="p-8 text-center text-sm" style={{ color: "var(--muted)" }}>No pending invoices.</div>}
          </div>
        </div>

        <div className="border p-6" style={{ borderColor: "var(--border)", background: "var(--ink)", color: "#F7F7F6" }}>
          <div className="eyebrow" style={{ color: "#87BBA7" }}>Fast actions</div>
          <div className="font-heading font-black text-2xl leading-none mt-3">Get things moving</div>
          <div className="mt-6 space-y-3">
            <Link to="/admin/users" className="block w-full px-4 py-3 text-sm font-medium" style={{ background: "#F7F7F6", color: "var(--ink)" }} data-testid="qa-add-user">Add Customer</Link>
            <Link to="/admin/setup" className="block w-full px-4 py-3 text-sm font-medium border" style={{ borderColor: "#87BBA7", color: "#F7F7F6" }} data-testid="qa-upload-census">Upload Census</Link>
            <Link to="/admin/setup#financials" className="block w-full px-4 py-3 text-sm font-medium border" style={{ borderColor: "#87BBA7", color: "#F7F7F6" }} data-testid="qa-upload-financials">Upload Program Financials</Link>
            <Link to="/admin/invoices" className="block w-full px-4 py-3 text-sm font-medium border" style={{ borderColor: "#87BBA7", color: "#F7F7F6" }} data-testid="qa-review-invoices">Review Invoices</Link>
            <Link to="/admin/audit" className="block w-full px-4 py-3 text-sm font-medium border" style={{ borderColor: "#87BBA7", color: "#F7F7F6" }} data-testid="qa-audit">Open Audit Trail</Link>
          </div>
        </div>
      </div>

      <BookSummary />
      <ProgramFinancials isAdmin />
    </div>
  );
}

const ROLE_OPTIONS = [
  { value: "customer", label: "Customer", hint: "Employer user — dashboard, invoices, analytics" },
  { value: "system_admin", label: "System Admin", hint: "Full access to every admin tool" },
  { value: "auditor", label: "Auditor", hint: "Audit logs only" },
  { value: "user_admin", label: "User Admin", hint: "Add, edit and delete users" },
  { value: "vendor_admin", label: "Vendor Admin", hint: "Manage the vendor catalog" },
  { value: "employer_admin", label: "Employer Admin", hint: "Manage employers and carriers" },
  { value: "financial_admin", label: "Financial Admin", hint: "Manage invoices" },
  { value: "data_admin", label: "Data Admin", hint: "Upload census, prevalence and feed files" },
];

const EMPLOYEES_FIRST = "Employees First";
const SYSTEM_ADMIN_ROLES = new Set([
  "system_admin",
  "user_admin",
  "vendor_admin",
  "employer_admin",
  "financial_admin",
  "data_admin",
]);
const hasSystemAdminRole = (roles) => roles.some((role) => SYSTEM_ADMIN_ROLES.has(role));

const roleLabel = (role) =>
  ROLE_OPTIONS.find((option) => option.value === role)?.label
  || (role === "admin" ? "System Admin" : role);

const suggestedUsername = (fullName) => {
  const parts = (fullName || "").toLowerCase().split(/\s+/).map((p) => p.replace(/[^a-z]/g, "")).filter(Boolean);
  if (!parts.length) return "";
  return (parts.length > 1 ? parts[0][0] + parts[parts.length - 1] : parts[0]).slice(0, 28);
};

function RolePicker({ value, onChange, testId }) {
  const toggle = (role) => {
    const next = value.includes(role) ? value.filter((r) => r !== role) : [...value, role];
    onChange(next.length ? next : ["customer"]);
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={testId}>
      {ROLE_OPTIONS.map((option) => (
        <label key={option.value} className="flex items-start gap-2.5 text-sm" style={{ color: "var(--ink)" }}>
          <input
            type="checkbox"
            className="mt-1"
            checked={value.includes(option.value)}
            onChange={() => toggle(option.value)}
            data-testid={`role-${option.value}`}
          />
          <span>
            {option.label}
            <span className="block text-[11px] leading-snug" style={{ color: "var(--muted)" }}>{option.hint}</span>
          </span>
        </label>
      ))}
    </div>
  );
}

/* --- Admin: Users --- */
export function AdminUsers() {
  const { user: viewer } = useAuth();
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [pendingOnly, setPendingOnly] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [emailEditorUser, setEmailEditorUser] = useState(null);
  const [updatedWorkEmail, setUpdatedWorkEmail] = useState("");
  const [editedName, setEditedName] = useState("");
  const [editedCompany, setEditedCompany] = useState("");
  const [editedRoles, setEditedRoles] = useState(["customer"]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    password: "", name: "", work_email: "", company: "", role: "customer",
    roles: ["customer"], payout_preference: "invoice_pay", send_invite: true,
  });

  const load = async () => {
    const { data } = await api.get("/admin/users");
    setUsers(data);
  };
  const loadCompanies = async () => {
    const { data } = await api.get("/admin/companies");
    const names = data.filter((c) => c.registered).map((c) => c.name);
    setCompanies([EMPLOYEES_FIRST, ...names.filter((name) => name !== EMPLOYEES_FIRST)]);
  };
  useEffect(() => {
    load().catch(e => toast.error(formatApiError(e.response?.data?.detail)));
    loadCompanies().catch(() => setCompanies([]));
  }, []);

  const submit = async (e, createOnly = false) => {
    e?.preventDefault();
    setSaving(true);
    try {
      if (form.roles.includes("customer") && !form.company) {
        toast.error("Choose the company this customer belongs to");
        setSaving(false);
        return;
      }
      if (!createOnly && !form.send_invite && !form.password) {
        toast.error("Set a password, send an invitation, or use Create Only");
        return;
      }
      await api.post("/admin/users", {
        ...form,
        create_only: createOnly,
        role: form.roles.some((r) => !["customer", "auditor"].includes(r)) ? "admin" : "customer",
        password: createOnly || form.send_invite ? null : form.password,
      });
      toast.success(createOnly
        ? "Inactive user created. Send an invitation when access should begin."
        : form.send_invite ? `Invitation emailed to ${form.work_email}` : "User created");
      setOpen(false);
      setForm({ password: "", name: "", work_email: "", company: "", role: "customer", roles: ["customer"], payout_preference: "invoice_pay", send_invite: true });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const sendInvite = async (user) => {
    try {
      const { data } = await api.post(`/admin/users/${user.id}/invite`);
      toast.success(`Invitation emailed to ${data.sent_to}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const revokeInvite = async (user) => {
    if (!window.confirm(`Revoke the pending invitation for ${user.name}?`)) return;
    try {
      await api.delete(`/admin/users/${user.id}/invite`);
      toast.success("Invitation link revoked");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resendAllPending = async () => {
    const pending = users.filter((u) => u.invite_pending);
    if (!pending.length) return;
    if (!window.confirm(`Resend invitations to all ${pending.length} pending users?`)) return;
    setSaving(true);
    let sent = 0;
    for (const user of pending) {
      try {
        await api.post(`/admin/users/${user.id}/invite`);
        sent += 1;
      } catch (e) {
        toast.error(`${user.name}: ${formatApiError(e.response?.data?.detail)}`);
      }
    }
    setSaving(false);
    if (sent) toast.success(`Resent ${sent} invitation${sent === 1 ? "" : "s"}`);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEmailEditor = (user) => {
    setEmailEditorUser(user);
    setUpdatedWorkEmail(user.work_email);
    setEditedName(user.name);
    setEditedCompany(user.company || "");
    setEditedRoles(user.roles?.length ? user.roles : [user.role === "admin" ? "system_admin" : "customer"]);
  };

  const saveWorkEmail = async (event) => {
    event.preventDefault();
    if (!emailEditorUser) return;
    setSaving(true);
    try {
      const payload = { name: editedName, company: editedCompany, roles: editedRoles };
      if (updatedWorkEmail.toLowerCase() !== (emailEditorUser.work_email || "").toLowerCase()) {
        payload.work_email = updatedWorkEmail;
      }
      const { data } = await api.put(`/admin/users/${emailEditorUser.id}`, payload);
      setUsers((current) => current.map((user) => (user.id === data.id ? data : user)));
      toast.success(
        payload.work_email
          ? "User updated. Sign-in email changed — sessions and trusted devices were revoked, and both addresses were notified."
          : "User updated"
      );
      setEmailEditorUser(null);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const pendingCount = users.filter((u) => u.invite_pending).length;

  const inviteAge = (iso) => {
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    if (days <= 0) return "Invited today";
    if (days === 1) return "Invited yesterday";
    return `Invited ${days} days ago`;
  };

  const visibleUsers = React.useMemo(() => {
    const term = userSearch.trim().toLowerCase();
    const base = pendingOnly ? users.filter((u) => u.invite_pending) : users;
    const matches = term
      ? base.filter((u) =>
          [u.name, u.username, u.work_email, u.company, u.role]
            .filter(Boolean)
            .some((field) => String(field).toLowerCase().includes(term)))
      : base;
    return [...matches].sort((a, b) =>
      (a.name || a.username || "").localeCompare(b.name || b.username || "", undefined,
        { sensitivity: "base" }) || (a.username || "").localeCompare(b.username || ""));
  }, [users, userSearch, pendingOnly]);
  const creatingSystemAdmin = hasSystemAdminRole(form.roles);
  const editingSystemAdmin = hasSystemAdminRole(editedRoles);

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10" data-testid="admin-users-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="eyebrow mb-2">Administration</div>
          <h1 className="font-heading font-black text-5xl leading-none tracking-tight" style={{ color: "var(--ink)" }}>Users</h1>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={() => setOpen(true)} data-testid="btn-add-user"><Plus size={14}/> Add User</button>
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--muted)" }}/>
          <input
            className="input-flat"
            style={{ paddingLeft: "2.25rem" }}
            placeholder="Search name, username, email or employer"
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            data-testid="user-search-input"
          />
        </div>
        <div className="text-xs" style={{ color: "var(--muted)" }} data-testid="user-search-count">
          {visibleUsers.length} of {pendingOnly ? pendingCount : users.length}{" "}
          {(pendingOnly ? pendingCount : users.length) === 1 ? "user" : "users"}
        </div>
        <button
          onClick={() => setPendingOnly((v) => !v)}
          className="btn-ghost flex items-center gap-2"
          style={pendingOnly ? { borderColor: "var(--ink)", color: "var(--ink)" } : {}}
          data-testid="toggle-pending-invites"
        >
          <MailPlus size={14}/> Pending invites ({pendingCount})
        </button>
        {pendingOnly && pendingCount > 0 && (
          <button
            onClick={resendAllPending}
            disabled={saving}
            className="btn-primary flex items-center gap-2"
            data-testid="resend-all-invites"
          >
            <Mail size={14}/> {saving ? "Sending…" : "Resend All"}
          </button>
        )}
      </div>

      <div className="border max-h-[560px] overflow-auto" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="users-table-region">
        <table className="w-full min-w-[860px] text-xs">
          <thead>
            <tr className="border-b eyebrow text-left" style={{ borderColor: "var(--border)" }}>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Username</th>
              <th className="px-3 py-2">Work email</th>
              <th className="px-3 py-2">Company</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {visibleUsers.map(u => (
              <tr key={u.id} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`user-row-${u.username}`}>
                <td className="px-3 py-2 font-medium" style={{ color: "var(--ink)" }}>{u.name}</td>
                <td className="px-3 py-2 font-mono text-[11px]" style={{ color: "var(--muted)" }}>{u.username}</td>
                <td className="px-3 py-2" style={{ color: "var(--muted)" }}>{u.work_email}</td>
                <td className="px-3 py-2" style={{ color: "var(--muted)" }}>{u.company}</td>
                <td className="px-3 py-2">
                  <span className="flex flex-wrap gap-1">
                    {(u.roles || [u.role]).map((role) => (
                      <span key={role} className="pill text-[10px]">{roleLabel(role)}</span>
                    ))}
                  </span>
                  {u.invite_pending && (
                    <span className="ml-2 inline-flex flex-col">
                      <span className="pill" data-testid={`invite-pending-${u.username}`}>Invite Pending</span>
                      <span className="mt-1 text-[10px]" style={{ color: "var(--muted)" }}
                            data-testid={`invite-age-${u.username}`}>
                        {u.invited_at ? inviteAge(u.invited_at) : "Invitation sent"}
                      </span>
                    </span>
                  )}
                  {!u.active && !u.invite_pending && (
                    <span className="ml-2 pill" data-testid={`inactive-user-${u.username}`}>
                      Inactive · Not Invited
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <span className="inline-flex items-center gap-1 justify-end">
                    <ActionIcon
                      icon={MailPlus}
                      label={u.invite_pending ? "Resend invitation" : "Send invitation to set a password"}
                      onClick={() => sendInvite(u)}
                      testId={`send-invite-${u.username}`}
                    />
                    {u.invite_pending && (
                      <ActionIcon icon={XCircle} label="Revoke pending invitation" onClick={() => revokeInvite(u)} testId={`revoke-invite-${u.username}`}/>
                    )}
                    <ActionIcon icon={Edit3} label="Edit name, work email and employer" onClick={() => openEmailEditor(u)} testId={`edit-email-${u.username}`}/>
                    <ActionIcon icon={Trash2} label="Delete user" onClick={() => remove(u.id)} testId={`del-user-${u.username}`}/>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="!rounded-none max-w-lg" style={{ borderColor: "var(--border)" }} data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-2xl tracking-tight">
              Add User
            </DialogTitle>
            <DialogDescription>
              Create a customer, administrator, or inactive pre-created account.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Full name" required><input className="input-flat" required data-testid="nu-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}/></Field>
              <Field label="Username (generated)">
                <input className="input-flat" readOnly disabled data-testid="nu-username" value={suggestedUsername(form.name)} placeholder="From the full name"/>
              </Field>
            </div>
            <Field label="Work email (locked)" required><input className="input-flat" type="email" required data-testid="nu-email" value={form.work_email} onChange={e => setForm({ ...form, work_email: e.target.value })}/></Field>
            <label className="flex items-start gap-3 text-sm" style={{ color: "var(--ink)" }}>
              <input
                type="checkbox"
                className="mt-1"
                checked={form.send_invite}
                onChange={e => setForm({ ...form, send_invite: e.target.checked })}
                data-testid="nu-send-invite"
              />
              <span>
                Email an invitation so they set their own password
                <span className="block text-xs" style={{ color: "var(--muted)" }}>
                  The link works once and expires in 72 hours.
                </span>
              </span>
            </label>
            <div className="grid grid-cols-2 gap-4">
              {!form.send_invite && (
                <Field label="Password" required><PasswordField minLength={12} required testId="nu-password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}/></Field>
              )}
              <Field label="Company" required>
                <Select value={form.company} onValueChange={v => setForm({ ...form, company: v })} disabled={creatingSystemAdmin}>
                  <SelectTrigger className="!rounded-none !border-x-0 !border-t-0 !bg-transparent !px-0" data-testid="nu-company">
                    <SelectValue placeholder="Select a company"/>
                  </SelectTrigger>
                  <SelectContent className="!rounded-none">
                    {companies.map(name => <SelectItem key={name} value={name}>{name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Roles" required>
              <RolePicker value={form.roles} onChange={(roles) => setForm({
                ...form,
                roles,
                company: hasSystemAdminRole(roles)
                  ? EMPLOYEES_FIRST
                  : form.company === EMPLOYEES_FIRST ? "" : form.company,
              })} testId="nu-roles"/>
            </Field>
            <DialogFooter>
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost">Cancel</button>
              <button type="button" disabled={saving} className="btn-ghost" onClick={() => submit(null, true)} data-testid="create-user-only">{saving ? "Saving..." : "Create Only"}</button>
              <button type="submit" disabled={saving} className="btn-primary" data-testid="submit-new-user">{saving ? "Saving..." : form.send_invite ? "Create & Invite" : "Create"}</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(emailEditorUser)}
        onOpenChange={(isOpen) => !isOpen && setEmailEditorUser(null)}
      >
        <DialogContent
          className="!rounded-none max-w-lg max-h-[90vh] overflow-y-auto"
          style={{ borderColor: "var(--border)" }}
          data-testid="edit-work-email-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-2xl tracking-tight">
              Edit User
            </DialogTitle>
            <DialogDescription>
              Update this account's name, work email and company.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm" style={{ color: "var(--muted)" }} data-testid="edit-work-email-notice">
            Future sign-in codes and password-recovery links will be sent to the work email below.
            Changing it signs this user out everywhere, clears their trusted browsers, and emails a
            notice to both the old and the new address.
          </p>
          <form onSubmit={saveWorkEmail} className="space-y-5" data-testid="edit-work-email-form">
            <Field label="Full name" required>
              <input
                className="input-flat"
                value={editedName}
                onChange={(event) => setEditedName(event.target.value)}
                required
                data-testid="edit-user-name-input"
              />
            </Field>
            <Field label="Work email" required>
              <input
                className="input-flat"
                type="email"
                value={updatedWorkEmail}
                onChange={(event) => setUpdatedWorkEmail(event.target.value)}
                autoComplete="email"
                required
                data-testid="edit-work-email-input"
              />
            </Field>
            <Field label="Roles" required>
              <RolePicker value={editedRoles} onChange={(roles) => {
                setEditedRoles(roles);
                if (hasSystemAdminRole(roles)) setEditedCompany(EMPLOYEES_FIRST);
              }} testId="edit-user-roles"/>
            </Field>
            <Field label="Company" required>
              <Select value={editedCompany} onValueChange={setEditedCompany} disabled={editingSystemAdmin}>
                <SelectTrigger className="!rounded-none !border-x-0 !border-t-0 !bg-transparent !px-0" data-testid="edit-user-company-input">
                  <SelectValue placeholder="Select a company"/>
                </SelectTrigger>
                <SelectContent className="!rounded-none">
                  {companies.map(name => <SelectItem key={name} value={name}>{name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            {emailEditorUser?.role === "admin" && (
              <MicrosoftLinkCard userId={emailEditorUser.id} isSelf={emailEditorUser.id === viewer?.id} />
            )}
            <DialogFooter>
              <button
                type="button"
                onClick={() => setEmailEditorUser(null)}
                className="btn-ghost"
                data-testid="cancel-edit-work-email"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="btn-primary"
                data-testid="save-work-email"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
}

/* --- Admin: Invoices --- */
export function AdminInvoices() {
  const paymentGroupsOn = useRelease().isOn("feature.payment_groups");
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoiceTab, setInvoiceTab] = useState("invoices");
  const [invoices, setInvoices] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [status, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [company, setCompany] = useState(searchParams.get("company") || "all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [activeInvoice, setActiveInvoice] = useState(null);
  const [statusEditor, setStatusEditor] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newInvoice, setNewInvoice] = useState({
    company: "", vendor_name: "", invoice_number: "", invoice_date: "", due_date: "",
    amount: "", description: "", ppr_amount: "", approve_now: false,
    historical: false, approver_name: "", approved_date: "", paid_date: "", ach_reference: "",
  });
  const [selected, setSelected] = useState([]);
  const [grouping, setGrouping] = useState(false);
  const [payForm, setPayForm] = useState({ file: null, ach_reference: "" });
  const [invoiceVendors, setInvoiceVendors] = useState([]);
  const [newAttachment, setNewAttachment] = useState(null);
  const [nextStatus, setNextStatus] = useState("approved");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/invoices", {
        params: {
          q: q || undefined,
          status: status === "all" ? undefined : status,
          company: company === "all" ? undefined : company,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          limit: INVOICE_PAGE_SIZE,
          skip: page * INVOICE_PAGE_SIZE,
        },
      });
      setInvoices(data.invoices);
      setTotal(data.total);
      setCompanies(data.companies || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [q, status, company, dateFrom, dateTo, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [q, status, company, dateFrom, dateTo]);

  const [editInvoice, setEditInvoice] = useState(null);
  const [editVendors, setEditVendors] = useState([]);

  const openEditInvoice = async (invoice) => {
    setEditInvoice({
      id: invoice.id,
      company: invoice.company || "",
      vendor_name: invoice.vendor_name || "",
      invoice_number: invoice.invoice_number || "",
      invoice_date: invoice.invoice_date || "",
      due_date: invoice.due_date || "",
      amount: String(invoice.amount ?? ""),
      description: invoice.description || "",
    });
    try {
      const { data } = await api.get("/admin/vendors", { params: { company: invoice.company } });
      setEditVendors(data.map((v) => v.name));
    } catch { setEditVendors([]); }
  };

  const saveEditInvoice = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/admin/invoices/${editInvoice.id}`, {
        vendor_name: editInvoice.vendor_name,
        invoice_number: editInvoice.invoice_number,
        invoice_date: editInvoice.invoice_date,
        due_date: editInvoice.due_date,
        amount: parseFloat(editInvoice.amount),
        description: editInvoice.description,
      });
      toast.success("Invoice updated");
      setEditInvoice(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const pickEmployer = async (name) => {
    setNewInvoice((current) => ({ ...current, company: name, vendor_name: "" }));
    try {
      const { data } = await api.get("/admin/vendors", { params: { company: name } });
      setInvoiceVendors(data.map((v) => v.name));
    } catch { setInvoiceVendors([]); }
  };

  const submitInvoice = async (event) => {
    event.preventDefault();
    if (!newInvoice.company || !newInvoice.vendor_name) {
      return toast.error("Choose an employer and a vendor");
    }
    if (!newAttachment) {
      return toast.error("Attach the vendor invoice before submitting");
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/invoices", {
        ...newInvoice,
        amount: parseFloat(newInvoice.amount),
        ppr_amount: newInvoice.ppr_amount ? parseFloat(newInvoice.ppr_amount) : 0,
      });
      toast.success(newInvoice.historical ? "Historical record saved as paid" : `Invoice created as ${data.status}`);
      if (newAttachment) {
        const fd = new FormData();
        fd.append("file", newAttachment);
        try {
          await api.post(`/invoices/${data.id}/attachments`, fd,
            { headers: { "Content-Type": "multipart/form-data" } });
          toast.success(`Attached ${newAttachment.name}`);
        } catch (uploadError) {
          toast.error(`Invoice saved, but the attachment failed: ${formatApiError(uploadError.response?.data?.detail)}`);
        }
      }
      setAddOpen(false);
      setNewAttachment(null);
      setNewInvoice({ company: "", vendor_name: "", invoice_number: "", invoice_date: "", due_date: "", amount: "", description: "", ppr_amount: "", approve_now: false, historical: false, approver_name: "", approved_date: "", paid_date: "", ach_reference: "" });
      setInvoiceVendors([]);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const openStatusEditor = (invoice, value) => {
    setStatusEditor(invoice);
    setNextStatus(value);
    setNote("");
    setPayForm({ file: null, ach_reference: invoice.ach_reference || "" });
  };

  const applyStatus = async () => {
    if (nextStatus === "paid" && !payForm.file && !window.confirm(
      "No bank confirmation report is attached. The notification email will go out without it. Continue?")) return;
    setSaving(true);
    try {
      if (nextStatus === "paid") {
        const fd = new FormData();
        fd.append("note", note);
        fd.append("ach_reference", payForm.ach_reference);
        if (payForm.file) fd.append("file", payForm.file);
        const { data } = await api.post(`/admin/invoices/${statusEditor.id}/mark-paid`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`Marked paid · ${data.notified_count} recipient(s) emailed`
          + (data.confirmation_attached ? " with the confirmation report" : ""));
      } else {
        const { data } = await api.put(`/admin/invoices/${statusEditor.id}`, { status: nextStatus, note });
        toast.success(data.notified ? `Marked ${nextStatus} · employer emailed` : `Marked ${nextStatus}`);
      }
      setStatusEditor(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const selectedRows = invoices.filter((inv) => selected.includes(inv.id));
  const sameVendor = new Set(selectedRows.map((inv) => `${inv.company}|${inv.vendor_name}`)).size <= 1;

  const toggleSelected = (id) => setSelected((current) =>
    current.includes(id) ? current.filter((value) => value !== id) : [...current, id]);

  const groupForPayment = async () => {
    setGrouping(true);
    try {
      await api.post("/admin/payments", { invoice_ids: selected });
      toast.success(`Grouped ${selected.length} invoice(s) into one payment`);
      setSelected([]);
      setInvoiceTab("payments");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setGrouping(false); }
  };

  const lastPage = Math.max(0, Math.ceil(total / INVOICE_PAGE_SIZE) - 1);

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10" data-testid="admin-invoices-page">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <div className="eyebrow mb-2">Administration</div>
          <h1 className="font-heading font-black text-5xl leading-none tracking-tight" style={{ color: "var(--ink)" }}>Invoices</h1>
        </div>
        <div className="flex items-center gap-3">
          <button className="btn-primary flex items-center gap-2" onClick={() => setAddOpen(true)} data-testid="btn-add-invoice"><Plus size={14}/> Add Invoice</button>
          <button className="btn-ghost flex items-center gap-2" onClick={load} data-testid="admin-invoices-refresh"><RefreshCw size={14}/> Refresh</button>
        </div>
      </div>

      <div className="flex gap-6 border-b mb-6" style={{ borderColor: "var(--border)" }} data-testid="admin-invoice-tabs">
        {[["invoices", "Invoices"], ...(paymentGroupsOn ? [["payments", "Grouped Payments"]] : []), ["ach", "ACH Batches"]].map(([key, label]) => (
          <button key={key} type="button" onClick={() => setInvoiceTab(key)}
            className="eyebrow pb-3 -mb-px transition-colors"
            style={{
              color: invoiceTab === key ? "var(--ink)" : "var(--muted)",
              borderBottom: invoiceTab === key ? "2px solid var(--ink)" : "2px solid transparent",
            }}
            data-testid={`admin-invoice-tab-${key}`}>
            {label}
          </button>
        ))}
      </div>

      {invoiceTab === "ach" ? <AdminAch /> : (invoiceTab === "payments" && paymentGroupsOn) ? <PaymentGroups onChanged={load} /> : (<>
      <div className="border p-6 mb-6" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-4">
            <label className="field-label">Search</label>
            <div className="relative">
              <Search size={14} className="absolute left-0 top-3.5" style={{ color: "var(--muted)" }}/>
              <input className="input-flat !pl-6" placeholder="Vendor, invoice # or employer" value={q}
                     onChange={(e) => setQ(e.target.value)} data-testid="invoice-search"/>
            </div>
          </div>
          <div className="lg:col-span-3">
            <label className="field-label">Employer</label>
            <Select value={company} onValueChange={setCompany}>
              <SelectTrigger className="!rounded-none !border-x-0 !border-t-0 !bg-transparent !px-0" data-testid="invoice-company-filter"><SelectValue/></SelectTrigger>
              <SelectContent className="!rounded-none max-h-72">
                <SelectItem value="all">All employers</SelectItem>
                {companies.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="lg:col-span-2">
            <label className="field-label">Status</label>
            <Select value={status} onValueChange={setStatusFilter}>
              <SelectTrigger className="!rounded-none !border-x-0 !border-t-0 !bg-transparent !px-0" data-testid="invoice-status-filter"><SelectValue/></SelectTrigger>
              <SelectContent className="!rounded-none">
                <SelectItem value="all">Any status</SelectItem>
                {INVOICE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="lg:col-span-1.5">
            <label className="field-label">From</label>
            <input type="date" className="input-flat" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="invoice-date-from"/>
          </div>
          <div className="lg:col-span-1.5">
            <label className="field-label">To</label>
            <input type="date" className="input-flat" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="invoice-date-to"/>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between text-sm">
          <span style={{ color: "var(--muted)" }} data-testid="invoice-total">{total} invoice{total === 1 ? "" : "s"} found</span>
          <button
            className="btn-ghost text-xs"
            onClick={() => { setQ(""); setStatusFilter("all"); setCompany("all"); setDateFrom(""); setDateTo(""); setSearchParams({}); }}
            data-testid="invoice-reset-filters"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {!!selected.length && (
        <div className="border p-4 mb-4 flex flex-wrap items-center justify-between gap-3"
             style={{ borderColor: "var(--ink)", background: "var(--surface)" }} data-testid="group-bar">
          <div className="text-sm" style={{ color: "var(--ink)" }}>
            <strong>{selected.length}</strong> approved invoice{selected.length === 1 ? "" : "s"} selected
            {" · "}{money(selectedRows.reduce((sum, inv) => sum + Number(inv.amount || 0), 0))}
            {!sameVendor && (
              <span className="block text-xs mt-1" style={{ color: "#9B2C1C" }} data-testid="group-bar-warning">
                A payment covers one employer and one vendor — deselect the others to group these.
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button className="btn-ghost text-xs" onClick={() => setSelected([])} data-testid="clear-selection">Clear</button>
            <button className="btn-primary text-xs flex items-center gap-1.5" disabled={!sameVendor || grouping}
                    onClick={groupForPayment} data-testid="group-for-payment">
              <Layers size={13} /> {grouping ? "Grouping..." : "Group for payment"}
            </button>
          </div>
        </div>
      )}

      <div className="border overflow-x-auto" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
        <table className="w-full text-sm min-w-[940px]">
          <thead>
            <tr className="border-b eyebrow text-left" style={{ borderColor: "var(--border)" }}>
              <th className="px-4 py-3 w-8"/>
              <th className="px-4 py-3">Vendor</th>
              <th className="px-4 py-3">Employer</th>
              <th className="px-4 py-3">Invoice</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Amount</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map(inv => (
              <tr key={inv.id} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`admin-invoice-${inv.id}`}>
                <td className="px-4 py-3">
                  {inv.groupable && (
                    <input type="checkbox" checked={selected.includes(inv.id)}
                           onChange={() => toggleSelected(inv.id)}
                           aria-label={`Select invoice ${inv.invoice_number} for payment`}
                           data-testid={`select-invoice-${inv.id}`} />
                  )}
                </td>
                <td className="px-4 py-3 font-medium" style={{ color: "var(--ink)" }}>
                  <span className="flex items-center gap-2">{inv.vendor_name}
                    {inv.fund_source && inv.fund_source !== "ERR" && (
                      <span className="pill text-[9px]" data-testid={`admin-fund-${inv.id}`}
                        title={inv.fund_source === "SPLIT" ? `ERR ${money(inv.err_amount)} + PPR ${money(inv.ppr_amount)}` : "Purchase Power Refunds"}>
                        {inv.fund_source}
                      </span>
                    )}
                    {inv.bank_status === "AWAITING_APPROVAL" && (
                      <span className="pill text-[9px]" style={{ color: "#B45309" }} data-testid={`admin-bank-status-${inv.id}`}
                        title="Submitted to J.P. Morgan Access — awaiting the second approver">
                        Awaiting bank approval
                      </span>
                    )}
                  </span>
                </td>
                <td className="px-4 py-3" style={{ color: "var(--muted)" }}>{inv.user_name}<div className="text-xs">{inv.company}</div></td>
                <td className="px-4 py-3 font-mono text-xs" style={{ color: "var(--muted)" }}>{inv.invoice_number}</td>
                <td className="px-4 py-3" style={{ color: "var(--muted)" }}>{inv.invoice_date}</td>
                <td className="px-4 py-3">
                  <StatusPill status={inv.status}/>
                  {inv.status === "pending" && !inv.has_approver && (
                    <div className="text-[10px] mt-1 font-bold uppercase tracking-wider" style={{ color: "#9B2C1C" }} data-testid={`no-approver-${inv.id}`}>
                      No approver assigned
                    </div>
                  )}
                  {inv.decided_by && (
                    <div className="text-[10px] mt-1" style={{ color: "var(--muted)" }}>by {inv.decided_by}</div>
                  )}
                  {inv.historical && (
                    <div className="text-[10px] mt-1 flex items-center gap-1" style={{ color: "var(--muted)" }} data-testid={`historical-${inv.id}`}>
                      <History size={10} /> Historical · {inv.legacy_source || "pre-portal"}
                    </div>
                  )}
                  {inv.payment_id && (
                    <div className="text-[10px] mt-1 flex items-center gap-1" style={{ color: "var(--muted)" }} data-testid={`grouped-${inv.id}`}>
                      <Layers size={10} /> In a grouped payment
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right font-heading font-bold" style={{ color: "var(--ink)" }}>{money(inv.amount)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <ActionIcon icon={Edit3} label="Edit invoice details" onClick={() => openEditInvoice(inv)} testId={`edit-invoice-${inv.id}`}/>
                    <TooltipProvider delayDuration={120}>
                      <UiTooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => setActiveInvoice(inv)}
                            className="btn-ghost !p-2 flex items-center gap-1 text-xs"
                            aria-label={`Documents for invoice ${inv.invoice_number}`}
                            data-testid={`admin-attachments-${inv.id}`}
                          >
                            <Paperclip size={14}/> {inv.attachment_count || 0}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="!rounded-none border text-xs font-medium" style={{ background: "var(--ink)", color: "#F7F7F6", borderColor: "var(--ink)" }}>
                          View or add invoice documents
                        </TooltipContent>
                      </UiTooltip>
                    </TooltipProvider>
                    <Select value={inv.status} onValueChange={(v) => openStatusEditor(inv, v)}>
                      <SelectTrigger className="w-32 !rounded-none h-8 text-xs" data-testid={`status-${inv.id}`}><SelectValue/></SelectTrigger>
                      <SelectContent className="!rounded-none">
                        {ADMIN_SETTABLE.map((s) => <SelectItem key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</SelectItem>)}
                        {!ADMIN_SETTABLE.includes(inv.status) && (
                          <SelectItem value={inv.status} disabled>{inv.status[0].toUpperCase() + inv.status.slice(1)}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </td>
              </tr>
            ))}
            {!invoices.length && <tr><td colSpan={8} className="p-12 text-center text-sm" style={{ color: "var(--muted)" }} data-testid="admin-invoices-empty">No invoices match these filters.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="mt-5 flex items-center justify-between">
        <span className="text-xs" style={{ color: "var(--muted)" }} data-testid="invoice-page-label">Page {page + 1} of {lastPage + 1}</span>
        <div className="flex gap-2">
          <button className="btn-ghost text-xs" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="invoice-prev">Previous</button>
          <button className="btn-ghost text-xs" disabled={page >= lastPage} onClick={() => setPage((p) => Math.min(lastPage, p + 1))} data-testid="invoice-next">Next</button>
        </div>
      </div>

      <Dialog open={Boolean(statusEditor)} onOpenChange={(isOpen) => !isOpen && setStatusEditor(null)}>
        <DialogContent className="!rounded-none max-w-lg" style={{ borderColor: "var(--border)" }} data-testid="status-change-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-2xl tracking-tight">Mark as {nextStatus}</DialogTitle>
            <DialogDescription>
              {statusEditor?.vendor_name} · {statusEditor?.invoice_number} · {statusEditor?.user_name}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm" style={{ color: "var(--muted)" }}>
            Approvals and rejections are made by the employer's designated approver; admins move invoices
            between pending and paid. {nextStatus === "paid"
              ? "Marking paid emails both the submitter and the approver, with the bank confirmation report attached."
              : "The employer is emailed automatically — add an optional note for that email."}
          </p>
          {nextStatus === "paid" && (
            <>
              <Field label="Bank confirmation report (PDF)">
                <input className="input-flat" type="file" accept="application/pdf"
                       onChange={(e) => setPayForm({ ...payForm, file: e.target.files?.[0] || null })}
                       data-testid="status-confirmation-file" />
                <p className="mt-2 text-xs" style={{ color: payForm.file ? "var(--muted)" : "#B45309" }}>
                  {payForm.file
                    ? `${payForm.file.name} · ${(payForm.file.size / 1024).toFixed(0)} KB — attached to the email`
                    : "Strongly recommended. Without it the email goes out with no proof of payment."}
                </p>
              </Field>
              <Field label="Bank reference">
                <input className="input-flat" value={payForm.ach_reference}
                       onChange={(e) => setPayForm({ ...payForm, ach_reference: e.target.value })}
                       placeholder="e.g. JPM ACH 884213" data-testid="status-reference" />
              </Field>
            </>
          )}
          <Field label="Note to client (optional)">
            <textarea
              className="input-flat resize-none"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Approved pending updated W-9"
              data-testid="status-note"
            />
          </Field>
          <DialogFooter>
            <button type="button" onClick={() => setStatusEditor(null)} className="btn-ghost" data-testid="cancel-status-change">Cancel</button>
            <button type="button" onClick={applyStatus} disabled={saving} className="btn-primary" data-testid="confirm-status-change">
              {saving ? "Saving..." : `Mark ${nextStatus} & notify`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editInvoice)} onOpenChange={(isOpen) => !isOpen && setEditInvoice(null)}>
        <DialogContent className="!rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" style={{ borderColor: "var(--border)" }} data-testid="edit-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-2xl tracking-tight">Edit Invoice</DialogTitle>
            <DialogDescription>{editInvoice?.company}</DialogDescription>
          </DialogHeader>
          {editInvoice && (
            <form onSubmit={saveEditInvoice} className="space-y-5">
              <Field label="Vendor" required>
                <Select value={editInvoice.vendor_name} onValueChange={(v) => setEditInvoice({ ...editInvoice, vendor_name: v })}>
                  <SelectTrigger className="!rounded-none !border-x-0 !border-t-0 !bg-transparent !px-0" data-testid="ei-vendor">
                    <SelectValue placeholder="Select a vendor"/>
                  </SelectTrigger>
                  <SelectContent className="!rounded-none max-h-72">
                    {editVendors.map((name) => <SelectItem key={name} value={name}>{name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Invoice number" required>
                  <input className="input-flat" required value={editInvoice.invoice_number} onChange={(e) => setEditInvoice({ ...editInvoice, invoice_number: e.target.value })} data-testid="ei-number"/>
                </Field>
                <Field label="Amount (USD)" required>
                  <input className="input-flat" type="number" step="0.01" min="0.01" required value={editInvoice.amount} onChange={(e) => setEditInvoice({ ...editInvoice, amount: e.target.value })} data-testid="ei-amount"/>
                </Field>
                <Field label="Invoice received date">
                  <input className="input-flat" type="date" value={editInvoice.invoice_date} onChange={(e) => setEditInvoice({ ...editInvoice, invoice_date: e.target.value })} data-testid="ei-date"/>
                </Field>
                <Field label="Invoice due date">
                  <input className="input-flat" type="date" value={editInvoice.due_date} onChange={(e) => setEditInvoice({ ...editInvoice, due_date: e.target.value })} data-testid="ei-due-date"/>
                </Field>
              </div>
              <Field label="Description">
                <textarea className="input-flat resize-none" rows={2} value={editInvoice.description} onChange={(e) => setEditInvoice({ ...editInvoice, description: e.target.value })} data-testid="ei-description"/>
              </Field>
              <div data-testid="ei-attachments">
                <div className="eyebrow mb-2">Invoice documents</div>
                <AttachmentsPanel invoice={editInvoice} previewHeight={360} onChanged={load} />
              </div>
              <DialogFooter>
                <button type="button" onClick={() => setEditInvoice(null)} className="btn-ghost" data-testid="cancel-edit-invoice">Cancel</button>
                <button type="submit" disabled={saving} className="btn-primary" data-testid="submit-edit-invoice">{saving ? "Saving..." : "Save Changes"}</button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="!rounded-none max-w-lg max-h-[90vh] overflow-y-auto" style={{ borderColor: "var(--border)" }} data-testid="add-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-2xl tracking-tight">
              {newInvoice.historical ? "Add Historical Record" : "Add Invoice"}
            </DialogTitle>
            <DialogDescription>
              {newInvoice.historical
                ? "Record a request and payment that ran through the old Adobe Sign workflow."
                : "Submit an invoice on behalf of an employer."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitInvoice} className="space-y-5">
            <Field label="Employer" required>
              <Select value={newInvoice.company} onValueChange={pickEmployer}>
                <SelectTrigger className="!rounded-none !border-x-0 !border-t-0 !bg-transparent !px-0" data-testid="ni-company">
                  <SelectValue placeholder="Select an employer"/>
                </SelectTrigger>
                <SelectContent className="!rounded-none max-h-72">
                  {companies.map((name) => <SelectItem key={name} value={name}>{name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Vendor" required>
              <Select value={newInvoice.vendor_name} onValueChange={(v) => setNewInvoice({ ...newInvoice, vendor_name: v })}>
                <SelectTrigger className="!rounded-none !border-x-0 !border-t-0 !bg-transparent !px-0" disabled={!newInvoice.company} data-testid="ni-vendor">
                  <SelectValue placeholder={newInvoice.company ? (invoiceVendors.length ? "Select a vendor" : "No vendors assigned") : "Pick an employer first"}/>
                </SelectTrigger>
                <SelectContent className="!rounded-none max-h-72">
                  {invoiceVendors.map((name) => <SelectItem key={name} value={name}>{name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Invoice number" required>
                <input className="input-flat" required value={newInvoice.invoice_number} onChange={(e) => setNewInvoice({ ...newInvoice, invoice_number: e.target.value })} data-testid="ni-number"/>
              </Field>
              <Field label="Invoice received date" required>
                <input className="input-flat" type="date" required value={newInvoice.invoice_date} onChange={(e) => setNewInvoice({ ...newInvoice, invoice_date: e.target.value })} data-testid="ni-date"/>
              </Field>
              <Field label="Invoice due date">
                <input className="input-flat" type="date" value={newInvoice.due_date} onChange={(e) => setNewInvoice({ ...newInvoice, due_date: e.target.value })} data-testid="ni-due-date"/>
              </Field>
            </div>
            <Field label="Amount (USD)" required>
              <input className="input-flat" type="number" step="0.01" min="0.01" required value={newInvoice.amount} onChange={(e) => setNewInvoice({ ...newInvoice, amount: e.target.value })} data-testid="ni-amount"/>
            </Field>
            <Field label="PPR portion (USD)">
              <input className="input-flat" type="number" step="0.01" min="0" value={newInvoice.ppr_amount} onChange={(e) => setNewInvoice({ ...newInvoice, ppr_amount: e.target.value })} data-testid="ni-ppr-amount"/>
              <p className="mt-2 text-xs" style={{ color: "var(--muted)" }}>
                Leave blank to draw the whole invoice on ERR funds. Anything entered here is paid from
                Purchase Power refunds instead.
              </p>
            </Field>
            <Field label="Description">
              <textarea className="input-flat resize-none" rows={2} value={newInvoice.description} onChange={(e) => setNewInvoice({ ...newInvoice, description: e.target.value })} data-testid="ni-description"/>
            </Field>
            <Field label="Invoice file (PDF, JPG or PNG)" required>
              <input
                className="input-flat"
                type="file"
                required
                accept="application/pdf,image/png,image/jpeg"
                onChange={(e) => setNewAttachment(e.target.files?.[0] || null)}
                data-testid="ni-attachment"
              />
              <p className="mt-2 text-xs" style={{ color: "var(--muted)" }}>
                {newAttachment
                  ? `${newAttachment.name} · ${(newAttachment.size / 1024).toFixed(0)} KB`
                  : "Required, 10 MB maximum. Attach the vendor's invoice so approvers can see it."}
              </p>
            </Field>
            <label className="flex items-start gap-3 text-sm" style={{ color: "var(--ink)" }}>
              <input
                type="checkbox"
                className="mt-1"
                checked={newInvoice.historical}
                onChange={(e) => setNewInvoice({ ...newInvoice, historical: e.target.checked, approve_now: false })}
                data-testid="ni-historical"
              />
              <span>
                Historical record (Adobe Sign era)
                <span className="block text-xs" style={{ color: "var(--muted)" }}>
                  Already requested, approved and paid outside the portal. Saved as paid, no emails are
                  sent, and it is kept out of the refund balance maths because the carrier feed already
                  includes it.
                </span>
              </span>
            </label>

            {newInvoice.historical ? (
              <div className="grid grid-cols-2 gap-4" data-testid="ni-historical-fields">
                <Field label="Approved by" required>
                  <input className="input-flat" required value={newInvoice.approver_name}
                         onChange={(e) => setNewInvoice({ ...newInvoice, approver_name: e.target.value })}
                         placeholder="Approver who signed the form" data-testid="ni-approver-name" />
                </Field>
                <Field label="Approved on">
                  <input className="input-flat" type="date" value={newInvoice.approved_date}
                         onChange={(e) => setNewInvoice({ ...newInvoice, approved_date: e.target.value })}
                         data-testid="ni-approved-date" />
                </Field>
                <Field label="Paid on">
                  <input className="input-flat" type="date" value={newInvoice.paid_date}
                         onChange={(e) => setNewInvoice({ ...newInvoice, paid_date: e.target.value })}
                         data-testid="ni-paid-date" />
                </Field>
                <Field label="Bank reference">
                  <input className="input-flat" value={newInvoice.ach_reference}
                         onChange={(e) => setNewInvoice({ ...newInvoice, ach_reference: e.target.value })}
                         placeholder="e.g. JPM ACH 771204" data-testid="ni-ach-reference" />
                </Field>
                <p className="col-span-2 text-xs" style={{ color: "var(--muted)" }}>
                  Attach the signed request form and the bank confirmation after saving, from the
                  documents button on the invoice row.
                </p>
              </div>
            ) : (
              <label className="flex items-start gap-3 text-sm" style={{ color: "var(--ink)" }}>
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={newInvoice.approve_now}
                  onChange={(e) => setNewInvoice({ ...newInvoice, approve_now: e.target.checked })}
                  data-testid="ni-approve-now"
                />
                <span>
                  Approve immediately
                  <span className="block text-xs" style={{ color: "var(--muted)" }}>
                    Leave unticked to send it to the employer's approval queue.
                  </span>
                </span>
              </label>
            )}
            <DialogFooter>
              <button type="button" onClick={() => setAddOpen(false)} className="btn-ghost" data-testid="cancel-add-invoice">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary" data-testid="submit-add-invoice">
                {saving ? "Saving..."
                  : newInvoice.historical ? "Save Historical Record"
                  : newInvoice.approve_now ? "Create & Approve" : "Create & Send For Approval"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AttachmentsDialog
        invoice={activeInvoice}
        onClose={() => setActiveInvoice(null)}
        onChanged={load}
      />
      </>)}
    </div>
  );
}

/* --- Admin: Census --- */
/** Plain-language age of a census upload; admin-facing only. */
function censusAge(iso) {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return "Uploaded today";
  if (days === 1) return "1 day ago";
  if (days < 31) return `${days} days ago`;
  const months = Math.floor(days / 30.44);
  if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
  const years = Math.floor(months / 12);
  return `${years} year${years === 1 ? "" : "s"} ago`;
}

export function AdminCensus() {
  const [companies, setCompanies] = useState([]);
  const [company, setCompany] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [matrixFile, setMatrixFile] = useState(null);
  const [matrixUploading, setMatrixUploading] = useState(false);
  const [matrixStatus, setMatrixStatus] = useState(null);
  const [employers, setEmployers] = useState([]);
  const { user } = useAuth();

  const load = async () => { const { data } = await api.get("/admin/census-companies"); setCompanies(data); };
  const loadEmployers = async () => {
    const { data } = await api.get("/admin/companies");
    setEmployers(data.filter((c) => c.registered).map((c) => c.name));
  };
  const loadMatrix = async () => { const { data } = await api.get("/admin/prevue-matrix"); setMatrixStatus(data); };
  useEffect(() => {
    load().catch(e => toast.error(formatApiError(e.response?.data?.detail)));
    loadMatrix().catch(() => setMatrixStatus(null));
    loadEmployers().catch(() => setEmployers([]));
  }, []);

  const uploadMatrix = async (e) => {
    e.preventDefault();
    if (!matrixFile) return toast.error("Choose a PreVue .xlsx workbook");
    setMatrixUploading(true);
    try {
      const fd = new FormData(); fd.append("file", matrixFile);
      const { data } = await api.post("/admin/prevue-matrix", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.imported} condition rows (AC, CI, HI)`);
      setMatrixFile(null);
      loadMatrix();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setMatrixUploading(false); }
  };

  const downloadTemplate = async (template) => {
    const isDob = template === "date_of_birth";
    try {
      await downloadFile(
        `/admin/census/template?template=${template}`,
        isDob ? "census-template-date-of-birth.csv" : "census-template-age.csv",
      );
      toast.success(isDob ? "Date of birth census template downloaded" : "Age census template downloaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file || !company) return toast.error("Provide an employer and a census CSV or Excel file");
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/admin/census/upload?company=${encodeURIComponent(company)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(
        data.skipped
          ? `Uploaded ${data.inserted} employees; skipped ${data.skipped} invalid row(s)`
          : `Uploaded ${data.inserted} employees`,
      );
      setFile(null); setCompany(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10" data-testid="admin-census-page">
      <div className="mb-8">
        <div className="eyebrow mb-2">Administration</div>
        <h1 className="font-heading font-black text-5xl leading-none tracking-tight" style={{ color: "var(--ink)" }}>Settings</h1>
        <p className="mt-3 text-[15px] max-w-2xl" style={{ color: "var(--muted)" }}>Financial feed, census, prevalence and rate management. Census accepts a CSV or Excel file with <span className="font-mono text-xs">gender</span> plus either <span className="font-mono text-xs">age</span> or <span className="font-mono text-xs">date_of_birth</span> to activate medical prevalence analytics for an employer.</p>
      </div>

      <section id="financials" data-testid="setup-financials-section">
        <h2 className="font-heading font-black text-3xl leading-none tracking-tight mb-4" style={{ color: "var(--ink)" }} data-testid="setup-financials-heading">
          Financials
        </h2>
        <AdminFinancials embedded />
      </section>

      <h2 className="font-heading font-black text-3xl leading-none tracking-tight mt-14 mb-4" style={{ color: "var(--ink)" }} data-testid="setup-census-heading">
        Census
      </h2>
      <form onSubmit={upload} className="border p-8 space-y-6" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Employer" required>
            <Select value={company} onValueChange={setCompany}>
              <SelectTrigger className="!rounded-none !border-x-0 !border-t-0 !bg-transparent !px-0" data-testid="census-company">
                <SelectValue placeholder="Select an employer"/>
              </SelectTrigger>
              <SelectContent className="!rounded-none max-h-72">
                {employers.map((name) => <SelectItem key={name} value={name}>{name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Census file (.csv or .xlsx)" required>
            <input type="file" accept=".csv,.xlsx" required onChange={e => setFile(e.target.files?.[0] || null)} data-testid="census-file"
                   className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:border file:border-solid file:text-sm file:font-semibold file:bg-transparent"/>
          </Field>
        </div>
        <p className="text-xs" style={{ color: "var(--muted)" }} data-testid="census-dob-note">
          When Date of Birth is supplied, the portal calculates age using the upload date.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <button type="submit" disabled={uploading} className="btn-primary" data-testid="submit-census">{uploading ? "Uploading..." : "Upload Census"}</button>
          <button type="button" onClick={() => downloadTemplate("age")} className="btn-ghost flex items-center gap-2" data-testid="census-template-download">
            <Download size={14}/> Download Age Template
          </button>
          <button type="button" onClick={() => downloadTemplate("date_of_birth")} className="btn-ghost flex items-center gap-2" data-testid="census-dob-template-download">
            <Download size={14}/> Download Date of Birth Template
          </button>
        </div>
      </form>

      <h2 className="font-heading font-black text-3xl leading-none tracking-tight mt-14 mb-4" style={{ color: "var(--ink)" }} data-testid="setup-prevalence-heading">
        Prevalence
      </h2>
      <form onSubmit={uploadMatrix} className="border p-8 space-y-6" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="prevue-matrix-form">
        <div>
          <div className="eyebrow">PreVue coverage matrix</div>
          <div className="font-heading font-bold text-xl mt-1" style={{ color: "var(--ink)" }}>Supplemental healthcare prevalence workbook</div>
          <p className="mt-3 text-[15px] max-w-2xl" style={{ color: "var(--muted)" }}>
            Upload the <span className="font-mono text-xs">.xlsx</span> matrix with columns <span className="font-mono text-xs">ConditionType</span> (AC, CI, HI), <span className="font-mono text-xs">ConditionName</span>, <span className="font-mono text-xs">CategoryType</span> and the <span className="font-mono text-xs">M / F</span> age-band rate columns. This replaces the current matrix used by PreVue Analytics.
          </p>
        </div>

        {matrixStatus && (
          <div className="flex flex-wrap gap-6 text-sm" data-testid="prevue-matrix-status">
            <div><span className="eyebrow block">Rows on file</span><span className="font-heading font-bold" style={{ color: "var(--ink)" }}>{matrixStatus.total}</span></div>
            {["AC", "CI", "HI", "OTH"].filter(t => matrixStatus.by_type?.[t] !== undefined).map(t => (
              <div key={t}><span className="eyebrow block">{t}</span><span className="font-heading font-bold" style={{ color: "var(--ink)" }}>{matrixStatus.by_type?.[t] ?? 0}</span></div>
            ))}
            {matrixStatus.imported_at && (
              <div><span className="eyebrow block">Last import</span><span style={{ color: "var(--muted)" }}>{new Date(matrixStatus.imported_at).toLocaleString()}</span></div>
            )}
          </div>
        )}

        <Field label="PreVue workbook (.xlsx)" required>
          <input
            type="file"
            accept=".xlsx"
            required
            onChange={e => setMatrixFile(e.target.files?.[0] || null)}
            data-testid="prevue-matrix-file"
            className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:border file:border-solid file:text-sm file:font-semibold file:bg-transparent"
          />
        </Field>
        <button type="submit" disabled={matrixUploading} className="btn-primary" data-testid="submit-prevue-matrix">
          {matrixUploading ? "Importing..." : "Import Prevue"}
        </button>
      </form>

      <div className="mt-10">
        <div className="eyebrow mb-3">Employers on file</div>
        <div className="border grid max-h-[420px] grid-cols-1 overflow-y-auto md:grid-cols-2 lg:grid-cols-3 grid-borders" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="census-employers-region">
          {companies.map(c => (
            <div key={c.company} className="grid-cell p-6" data-testid={`census-employer-${c.company}`}>
              <div className="eyebrow">{c.company}</div>
              <div className="stat-num text-3xl mt-2">{c.employees}</div>
              <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>employees</div>
              <div className="text-xs mt-3 pt-3 border-t" style={{ borderColor: "var(--border)", color: "var(--muted)" }} data-testid={`census-uploaded-${c.company}`}>
                {c.uploaded_at ? (
                  <>
                    Uploaded {new Date(c.uploaded_at).toLocaleDateString("en-US", { dateStyle: "medium" })}
                    <span className="block mt-0.5">
                      {censusAge(c.uploaded_at)}{c.uploaded_by ? ` · ${c.uploaded_by}` : ""}
                    </span>
                    {c.source_file && <span className="block font-mono text-[10px] mt-0.5">{c.source_file}</span>}
                  </>
                ) : "Upload date not recorded"}
              </div>
            </div>
          ))}
          {!companies.length && <div className="p-8 text-center text-sm col-span-full" style={{ color: "var(--muted)" }}>No census uploaded yet.</div>}
        </div>
      </div>

      <h2 className="font-heading font-black text-3xl leading-none tracking-tight mt-14 mb-4" style={{ color: "var(--ink)" }} data-testid="setup-rates-heading">
        Rates
      </h2>
      <RatesManager />

      {user?.permissions?.includes("system") && (
        <section id="book-summary-visibility" className="scroll-mt-28" data-testid="setup-book-visibility-section">
          <h2 className="font-heading font-black text-3xl leading-none tracking-tight mt-14 mb-4" style={{ color: "var(--ink)" }}>
            Book summary employers
          </h2>
          <BookVisibilityCard />
        </section>
      )}

      {user?.permissions?.includes("system") && (
        <section id="signin-panel" data-testid="setup-signin-panel-section">
          <h2 className="font-heading font-black text-3xl leading-none tracking-tight mt-14 mb-4" style={{ color: "var(--ink)" }} data-testid="setup-signin-panel-heading">
            Sign-in panel
          </h2>
          <SigninPanelCard />
        </section>
      )}

      {user?.permissions?.includes("system") && (
        <section id="go-live" className="scroll-mt-28" data-testid="setup-golive-section">
          <h2 className="font-heading font-black text-3xl leading-none tracking-tight mt-14 mb-4" style={{ color: "var(--ink)" }}>
            Go live
          </h2>
          <GoLiveCard />
        </section>
      )}

      {user?.permissions?.includes("system") && (
        <section id="linked-site" className="scroll-mt-28" data-testid="setup-linked-site-section">
          <h2 className="font-heading font-black text-3xl leading-none tracking-tight mt-14 mb-4" style={{ color: "var(--ink)" }}>
            Linked site
          </h2>
          <LinkedSiteCard />
        </section>
      )}

    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub }) {
  return (
    <div className="grid-cell p-6">
      <div className="eyebrow flex items-center gap-2">{Icon && <Icon size={12}/>} {label}</div>
      <div className="stat-num text-3xl md:text-4xl mt-2">{value}</div>
      {sub && <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>{sub}</div>}
    </div>
  );
}
function Field({ label, required, children }) {
  return (
    <div>
      <label className="field-label">{label}{required && " *"}</label>
      {children}
    </div>
  );
}
